'use client';

import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import {
  Sun,
  CloudSun,
  Cloud,
  CloudRain,
  CloudSnow,
  CloudDrizzle,
  CloudHail,
  CloudLightning,
  CloudFog,
  Wind,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { weatherIconMap } from '@/data/weather';
import {
  resolveWeatherLabels,
  type WeatherLabels,
} from '@/components/weather/labels';
import { useLocale } from 'next-intl';
import type { LucideIcon } from 'lucide-react';

const iconComponents: Record<string, LucideIcon> = {
  Sun,
  CloudSun,
  Cloud,
  CloudRain,
  CloudSnow,
  CloudDrizzle,
  CloudHail,
  CloudLightning,
  CloudFog,
};

interface ForecastPoint {
  timestamp: string;
  temperature: number | null;
  windSpeed: number | null;
  windDirection: number | null;
  humidity: number | null;
  precipitation: number | null;
  weatherSymbol: number | null;
}

interface DaySummary {
  date: string;
  dayName: string;
  high: number;
  low: number;
  windSpeed: number;
  precipitation: number;
  weatherSymbol: number | null;
}

interface ForecastData {
  levi: ForecastPoint[];
  yllas: ForecastPoint[];
}

function getMode(arr: (number | null)[]): number | null {
  const counts = new Map<number, number>();
  for (const val of arr) {
    if (val != null) {
      counts.set(val, (counts.get(val) || 0) + 1);
    }
  }
  let maxCount = 0;
  let mode: number | null = null;
  for (const [val, count] of counts) {
    if (count > maxCount) {
      maxCount = count;
      mode = val;
    }
  }
  return mode;
}

function summarizeDays(points: ForecastPoint[], intlLocale: string): DaySummary[] {
  const grouped = new Map<string, ForecastPoint[]>();

  for (const point of points) {
    const dateKey = new Date(point.timestamp).toISOString().split('T')[0];
    if (!grouped.has(dateKey)) {
      grouped.set(dateKey, []);
    }
    grouped.get(dateKey)!.push(point);
  }

  const summaries: DaySummary[] = [];
  for (const [dateKey, dayPoints] of grouped) {
    const temps = dayPoints.map((p) => p.temperature).filter((t): t is number => t !== null);
    const winds = dayPoints.map((p) => p.windSpeed).filter((w): w is number => w !== null);
    const dayName = new Date(dateKey + 'T12:00:00').toLocaleDateString(intlLocale, {
      weekday: 'short',
    });

    if (temps.length === 0) continue;

    summaries.push({
      date: dateKey,
      dayName,
      high: Math.round(Math.max(...temps)),
      low: Math.round(Math.min(...temps)),
      windSpeed: winds.length > 0 ? Math.round(Math.max(...winds)) : 0,
      precipitation: Math.round(dayPoints.reduce((sum, p) => sum + (p.precipitation ?? 0), 0) * 10) / 10,
      weatherSymbol: getMode(dayPoints.map((p) => p.weatherSymbol)),
    });
  }

  return summaries.slice(0, 5);
}

function WeatherIcon({ symbol }: { symbol: number | null }) {
  const iconName = symbol != null ? weatherIconMap[symbol] : undefined;
  const IconComponent = iconName ? iconComponents[iconName] : Cloud;
  const Resolved = IconComponent || Cloud;
  return <Resolved className="h-8 w-8" />;
}

function SkeletonCard() {
  return (
    <div className="w-24 rounded-xl p-3 animate-pulse bg-[#F0EDE5]">
      <div className="h-4 w-12 rounded mb-2 mx-auto bg-gray-200" />
      <div className="h-8 w-8 rounded-full mb-2 mx-auto bg-gray-200" />
      <div className="h-4 w-16 rounded mb-1 mx-auto bg-gray-200" />
      <div className="h-3 w-12 rounded mx-auto bg-gray-200" />
    </div>
  );
}

function DayCard({ day }: { day: DaySummary }) {
  return (
    <motion.div
      whileHover={{ scale: 1.03 }}
      className="w-24 rounded-xl p-3 text-center bg-white border border-[#F0EDE5] shadow-sm"
    >
      <p className="font-sans text-xs font-medium uppercase tracking-wide mb-2 text-[#5A5A6E]">
        {day.dayName}
      </p>
      <div className="flex justify-center mb-2 text-[#1A1A2E]">
        <WeatherIcon symbol={day.weatherSymbol} />
      </div>
      <p className="font-sans text-sm font-semibold mb-1 text-[#1A1A2E]">
        {day.high}° / {day.low}°
      </p>
      <div className="flex items-center justify-center gap-1 text-xs mb-1 text-[#5A5A6E]">
        <Wind className="h-3 w-3" />
        <span>{day.windSpeed} m/s</span>
      </div>
      <p className="font-sans text-xs text-[#5A5A6E]">
        {day.precipitation} mm
      </p>
    </motion.div>
  );
}

export default function ForecastCards({ labels }: { labels?: WeatherLabels }) {
  const L = resolveWeatherLabels(labels);
  const locale = useLocale();
  const intlLocale = locale === 'en' ? 'en-GB' : locale;
  const [data, setData] = useState<ForecastData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    fetch('/api/weather/forecast', { cache: 'no-store' })
      .then((res) => {
        if (!res.ok) throw new Error('Fetch failed');
        return res.json();
      })
      .then((json) => {
        setData(json);
        setLoading(false);
      })
      .catch(() => {
        setError(true);
        setLoading(false);
      });
  }, []);

  if (error) {
    return (
      <div className="rounded-xl p-6 text-center bg-white text-[#5A5A6E]">
        {L.forecastUnavailable}
      </div>
    );
  }

  const resorts: { key: 'levi' | 'yllas'; label: string }[] = [
    { key: 'levi', label: 'Levi' },
    { key: 'yllas', label: 'Ylläs' },
  ];

  return (
    <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-10">
      {resorts.map((resort) => {
        const days = data ? summarizeDays(data[resort.key], intlLocale) : [];

        return (
          <motion.div
            key={resort.key}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="text-center"
          >
            <h3 className="font-sans text-xl font-light mb-4 text-[#1A1A2E]">
              {resort.label}
            </h3>
            <div className="flex justify-center gap-3 flex-wrap">
              {loading
                ? Array.from({ length: 5 }).map((_, i) => (
                    <SkeletonCard key={i} />
                  ))
                : days.map((day) => (
                    <DayCard key={day.date} day={day} />
                  ))}
            </div>
          </motion.div>
        );
      })}
    </div>
  );
}
